/**
 * The account menu, top right. Screen spec 2.5, built as drawn: identity,
 * the plan with its account rollup bar, the Simple and Advanced switch, and
 * three destinations. 280px.
 *
 * WHY THE SWITCH IS HERE AS WELL AS THE RAIL FOOT. The rail foot exists so the
 * switch is one click away on a project screen. The top bar exists on every
 * screen INCLUDING the ones without a rail (the projects list, account), so
 * spec 2.5 puts the same control in the avatar menu and the two stay one
 * state through useAdvanced. Two mounts, one truth, and the consequence
 * sentence is always present at both, never on hover.
 *
 * INTERACTION IDIOM IS HelpMenu's, deliberately: the same anchor wrapper, the
 * same mousedown-outside close, the same Escape and arrow behaviour, focus
 * into the first control on open and back onto the avatar on close. Two
 * popovers in one top bar that close differently would each make the other
 * feel broken.
 *
 * SIGN OUT IS A SEAM, NOT A FEATURE, TODAY. The real backend already ships
 * POST /auth/signout against a bearer session (see SV-CLOUD-BACKEND.md), so
 * the button exists and does the honest demo thing: it walks back to the
 * landing page. When the real auth slice lands, this one handler exchanges
 * the walk for the POST plus dropping the stored session, and nothing else
 * in the menu changes. Do not wire it deeper into the mock than this: a
 * pretend session teardown would be demo theatre with a real looking name.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { getUsage, signOut } from '../../lib/api';
import { DETAIL_TOGGLE_ENABLED, useAdvanced } from '../../lib/hooks/use-advanced';
import { t } from '../../lib/i18n/t';
import { captureEvent } from '../../lib/analytics/posthog';
import type { Account, Usage } from '../../lib/types';
import { DOCS_URL } from '../patterns/CommandPalette';
import { UsageBar } from '../patterns/UsageBar';

/** Whole days until the metering period resets, never negative. */
function daysUntil(iso: string): number {
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / 86400000));
}

const PLAN_LABEL: Record<Usage['plan'], string> = {
  basic: 'Basic plan',
  pro: 'Pro plan',
  max: 'Max plan',
};

export function AccountPopover({ account }: { account: Account }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [advanced, setAdvanced] = useAdvanced();
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const close = useCallback((returnFocus = true) => {
    setOpen(false);
    if (returnFocus) buttonRef.current?.focus();
  }, []);

  // The rollup is fetched when the menu opens, not when the shell mounts:
  // the number is only on screen while the menu is, and the projects screen
  // already asks the same question for its own ACROSS YOUR PROJECTS band.
  useEffect(() => {
    if (!open) return;
    captureEvent('account_menu_opened');
    let stale = false;
    getUsage(null).then((rollup) => {
      if (!stale) setUsage(rollup);
    });
    return () => {
      stale = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onDown(event: MouseEvent) {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    menuRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
  }, [open]);

  const onMenuKey = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        close();
        return;
      }
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      event.preventDefault();
      const nodes = Array.from(
        menuRef.current?.querySelectorAll<HTMLButtonElement>('button, a') ?? [],
      );
      if (nodes.length === 0) return;
      const at = nodes.indexOf(document.activeElement as HTMLButtonElement);
      const step = event.key === 'ArrowDown' ? 1 : -1;
      const next = (at + step + nodes.length) % nodes.length;
      nodes[next].focus();
    },
    [close],
  );

  const go = useCallback(
    (to: string) => {
      close(false);
      navigate(to);
    },
    [close, navigate],
  );

  const compute = usage?.metrics.find((m) => m.resource === 'compute');

  return (
    <div className="omni__anchor" ref={wrapRef}>
      <button
        type="button"
        ref={buttonRef}
        className="topbar__avatar acctpop__button"
        data-tour="account-menu"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('Account menu')}
        onClick={() => setOpen((was) => !was)}
      >
        {account.name.slice(0, 1)}
      </button>

      {open ? (
        <div
          className="acctpop"
          role="menu"
          aria-label={t('Account')}
          ref={menuRef}
          onKeyDown={onMenuKey}
        >
          <div className="acctpop__who">
            <span className="acctpop__name">{account.name}</span>
            <span className="acctpop__email mono">{account.email}</span>
          </div>

          {usage ? (
            <div className="acctpop__plan">
              <span className="acctpop__plan-name">{t(PLAN_LABEL[usage.plan])}</span>
              {compute ? (
                <UsageBar
                  label={t('Compute Usage')}
                  used={compute.used}
                  limit={compute.limit}
                  compact
                  // The unit is abstract, so the percentage is the only
                  // reading that means anything: it replaces the amount
                  // rather than sitting beside it.
                  showPercent
                  percentOnly
                  resetsInDays={daysUntil(usage.resetsAt)}
                />
              ) : null}
            </div>
          ) : null}

          {DETAIL_TOGGLE_ENABLED ? (
          <div className="acctpop__details">
            <div className="segmented" role="group" aria-label={t('Level of detail')}>
              <button
                type="button"
                className="segmented__option"
                aria-pressed={!advanced}
                onClick={() => {
                  captureEvent('detail_level_toggled', { level: 'simple', source: 'account_popover' });
                  setAdvanced(false);
                }}
              >
                {t('Simple')}
              </button>
              <button
                type="button"
                className="segmented__option"
                aria-pressed={advanced}
                onClick={() => {
                  captureEvent('detail_level_toggled', { level: 'advanced', source: 'account_popover' });
                  setAdvanced(true);
                }}
              >
                {t('Advanced')}
              </button>
            </div>
            <p className="segmented__note">
              {t('Advanced adds technical details. It never hides anything.')}
            </p>
          </div>
          ) : null}

          <div className="acctpop__nav">
            <button
              type="button"
              className="helpmenu__item"
              role="menuitem"
              onClick={() => {
                captureEvent('account_settings_navigated');
                go('/account');
              }}
            >
              {t('Account settings')}
            </button>
            <a
              className="helpmenu__item"
              role="menuitem"
              href={DOCS_URL}
              target="_blank"
              rel="noreferrer"
              onClick={() => {
                captureEvent('account_popover_docs_clicked');
                close(false);
              }}
            >
              {t('Documentation')}
              <span className="helpmenu__away" aria-hidden="true">
                {'↗'}
              </span>
            </a>
            <button
              type="button"
              className="helpmenu__item"
              role="menuitem"
              onClick={async () => {
                captureEvent('sign_out_clicked');
                close(false);
                await signOut();
                navigate('/');
              }}
            >
              {t('Sign out')}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
